<template>
  <div v-if="visible" class="loading-container" :class="{ 'fullscreen': fullscreen, [`size-${size}`]: size }">
    <div class="loading-content">
      <!-- 加载动画 -->
      <div class="loading-spinner" :class="[`spinner-${type}`]">
        <!-- 简洁旋转圆圈类型 -->
        <template v-if="type === 'circle'">
          <div class="spinner-circle">
            <div class="circle-ring"></div>
          </div>
        </template>

        <!-- 波浪类型 -->
        <template v-else-if="type === 'wave'">
          <div class="spinner-wave">
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </template>

        <!-- 线条类型 -->
        <template v-else-if="type === 'line'">
          <div class="spinner-line">
            <div class="line-item"></div>
            <div class="line-item"></div>
            <div class="line-item"></div>
            <div class="line-item"></div>
          </div>
        </template>

        <!-- 骨架屏类型 -->
        <template v-else-if="type === 'skeleton'">
          <div class="spinner-skeleton">
            <div class="skeleton-bar"></div>
            <div class="skeleton-bar"></div>
            <div class="skeleton-bar short"></div>
          </div>
        </template>
      </div>

      <!-- 加载文字 -->
      <p v-if="text" class="loading-text">{{ text }}</p>
    </div>

    <!-- 遮罩层 -->
    <div v-if="fullscreen" class="loading-mask"></div>
  </div>
</template>

<script>
export default {
  name: 'Loading',
  props: {
    // 是否显示
    visible: {
      type: Boolean,
      default: false
    },
    // 加载文字
    text: {
      type: String,
      default: '加载中...'
    },
    // 加载类型：circle(简洁圆圈), wave(波浪), line(线条), skeleton(骨架屏)
    type: {
      type: String,
      default: 'wave',
      validator: (value) => ['circle', 'wave', 'line', 'skeleton'].includes(value)
    },
    // 尺寸：small, medium, large
    size: {
      type: String,
      default: 'medium',
      validator: (value) => ['small', 'medium', 'large'].includes(value)
    },
    // 是否全屏显示
    fullscreen: {
      type: Boolean,
      default: false
    }
  }
}
</script>

<style lang="scss" scoped>
.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 40px 20px;
  position: relative;

  &.fullscreen {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 9999;
    padding: 0;
  }

  // 尺寸变体
  &.size-small {
    padding: 20px;

    .loading-spinner {
      width: 32px;
      height: 32px;
    }

    .loading-text {
      font-size: 13px;
      margin-top: 12px;
    }
  }

  &.size-medium {
    .loading-spinner {
      width: 40px;
      height: 40px;
    }

    .loading-text {
      font-size: 14px;
      margin-top: 16px;
    }
  }

  &.size-large {
    padding: 60px 20px;

    .loading-spinner {
      width: 50px;
      height: 50px;
    }

    .loading-text {
      font-size: 16px;
      margin-top: 20px;
    }
  }
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1;
  position: relative;
}

.loading-mask {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(3px);
}

// 简洁旋转圆圈类型
.spinner-circle {
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  .circle-ring {
    width: 100%;
    height: 100%;
    border: 3px solid rgba($primary-color, 0.15);
    border-top-color: $primary-color;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
}

// 波浪类型
.spinner-wave {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  height: 100%;

  span {
    width: 4px;
    height: 100%;
    background: linear-gradient(to top, $primary-color, rgba($primary-color, 0.3));
    border-radius: 2px;
    animation: wave 1.2s ease-in-out infinite;

    &:nth-child(1) {
      animation-delay: 0s;
    }

    &:nth-child(2) {
      animation-delay: 0.15s;
    }

    &:nth-child(3) {
      animation-delay: 0.3s;
    }

    &:nth-child(4) {
      animation-delay: 0.45s;
    }

    &:nth-child(5) {
      animation-delay: 0.6s;
    }
  }
}

// 线条类型
.spinner-line {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  width: 100%;
  height: 100%;

  .line-item {
    width: 3px;
    height: 100%;
    background-color: $primary-color;
    border-radius: 2px;
    animation: line-stretch 1s ease-in-out infinite;

    &:nth-child(1) {
      animation-delay: 0s;
    }

    &:nth-child(2) {
      animation-delay: 0.2s;
    }

    &:nth-child(3) {
      animation-delay: 0.4s;
    }

    &:nth-child(4) {
      animation-delay: 0.6s;
    }
  }
}

// 骨架屏类型
.spinner-skeleton {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 100%;

  .skeleton-bar {
    width: 100%;
    height: 8px;
    background: linear-gradient(
      90deg,
      rgba($primary-color, 0.1) 0%,
      rgba($primary-color, 0.3) 50%,
      rgba($primary-color, 0.1) 100%
    );
    background-size: 200% 100%;
    border-radius: 4px;
    animation: skeleton-shimmer 1.5s ease-in-out infinite;

    &.short {
      width: 60%;
    }
  }
}

.loading-text {
  color: #999999;
  margin: 0;
  font-weight: 400;
  line-height: 1.5;
  animation: fadeIn 0.3s ease-out;
  letter-spacing: 0.5px;
}

// 动画定义
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes wave {
  0%, 100% {
    transform: scaleY(0.4);
    opacity: 0.5;
  }
  50% {
    transform: scaleY(1);
    opacity: 1;
  }
}

@keyframes line-stretch {
  0%, 100% {
    transform: scaleY(0.3);
    opacity: 0.4;
  }
  50% {
    transform: scaleY(1);
    opacity: 1;
  }
}

@keyframes skeleton-shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

// 响应式设计
@media screen and (max-width: 768px) {
  .loading-container {
    padding: 30px 16px;

    &.size-small {
      padding: 16px;

      .loading-spinner {
        width: 28px;
        height: 28px;
      }

      .loading-text {
        font-size: 12px;
        margin-top: 10px;
      }
    }

    &.size-medium {
      .loading-spinner {
        width: 36px;
        height: 36px;
      }

      .loading-text {
        font-size: 13px;
        margin-top: 14px;
      }
    }

    &.size-large {
      padding: 40px 16px;

      .loading-spinner {
        width: 44px;
        height: 44px;
      }

      .loading-text {
        font-size: 15px;
        margin-top: 18px;
      }
    }
  }

  .spinner-circle .circle-ring {
    border-width: 2.5px;
  }

  .spinner-wave {
    gap: 3px;

    span {
      width: 3px;
    }
  }

  .spinner-line {
    gap: 2px;

    .line-item {
      width: 2.5px;
    }
  }

  .spinner-skeleton {
    gap: 6px;

    .skeleton-bar {
      height: 6px;
    }
  }
}
</style>
